import React from 'react';
import SearchableSelect from '../SearchableSelect';
import MultiSelect from './MultiSelect';
import DateBound from './DateBound';


const inputCls = 'shadow-sm border border-slate-300 rounded px-2 py-1.5 text-sm w-full';

const unitLabel = (unit) => (unit === 'usd' ? 'USD' : 'local currency');

const defaultValue = (attribute) => {
  switch (attribute.type) {
    case 'boolean':
      return true;
    case 'enum':
      return [];
    case 'date':
      return { from: '', to: '' };
    case 'integer':
      return { ranges: [{ from: '', to: '' }] };
    case 'money':
      return { from: '', to: '' };
    default:
      return '';
  }
};

const newCondition = (attribute) => {
  const condition = {
    attribute: attribute.id,
    operator: attribute.operators[0],
    value: defaultValue(attribute)
  };
  if (attribute.units && attribute.units.length) condition.unit = attribute.units[0];
  if (attribute.windows && attribute.windows.length) condition.window = attribute.windows[0];
  return condition;
};

const RangeRows = ({ value, onChange }) => {
  const ranges = value?.ranges?.length ? value.ranges : [{ from: '', to: '' }];
  const update = (i, key, v) =>
    onChange({ ranges: ranges.map((r, idx) => (idx === i ? { ...r, [key]: v } : r)) });

  return (
    <div className='space-y-1.5'>
      {ranges.map((range, i) => (
        <div key={i} className='flex items-center gap-2'>
          <input
            type='number'
            min='0'
            className={inputCls}
            placeholder='from'
            value={range.from ?? ''}
            onChange={(e) => update(i, 'from', e.target.value)}
          />
          <span className='text-slate-400 text-xs'>—</span>
          <input
            type='number'
            min='0'
            className={inputCls}
            placeholder='to'
            value={range.to ?? ''}
            onChange={(e) => update(i, 'to', e.target.value)}
          />
          {ranges.length > 1 && (
            <button
              type='button'
              className='text-slate-400 hover:text-red-600 text-sm px-1'
              onClick={() => onChange({ ranges: ranges.filter((_, idx) => idx !== i) })}
            >
              ×
            </button>
          )}
        </div>
      ))}
      <button
        type='button'
        className='text-xs text-blue-600 hover:text-blue-800'
        onClick={() => onChange({ ranges: [...ranges, { from: '', to: '' }] })}
      >
        + one more range (OR)
      </button>
    </div>
  );
};

const ValueEditor = ({ attribute, condition, options, unavailable, onChange }) => {
  const setValue = (value) => onChange({ ...condition, value });

  switch (attribute.type) {
    case 'boolean':
      return (
        <select
          className={inputCls}
          value={condition.value ? 'yes' : 'no'}
          onChange={(e) => setValue(e.target.value === 'yes')}
        >
          <option value='yes'>Yes</option>
          <option value='no'>No</option>
        </select>
      );

    case 'enum': {
      const values = options[attribute.options_from] || [];
      // Пустой список и недоступный источник выглядели одинаково («No options»),
      // и человек не понимал, почему значение требуется, но выбрать нечего.
      const reason = unavailable?.[attribute.options_from];

      return (
        <div>
          <MultiSelect
            options={values}
            value={condition.value || []}
            onChange={setValue}
            placeholder={reason ? 'Values unavailable' : 'Select values…'}
            disabled={Boolean(reason)}
          />
          {reason && (
            <p className='text-xs text-amber-700 bg-amber-50 rounded px-2 py-1.5 mt-1'>
              Could not read the values from the source database, so this condition can't be used
              yet. Remove it or try again later. Reason: {reason}
            </p>
          )}
          {!reason && values.length === 0 && (
            <p className='text-xs text-slate-500 mt-1'>
              The source database has no values for this attribute.
            </p>
          )}
        </div>
      );
    }

    case 'date':
      return (
        <div className='grid grid-cols-2 gap-2'>
          <DateBound
            label='From'
            value={condition.value?.from ?? ''}
            onChange={(v) => setValue({ ...(condition.value || {}), from: v })}
          />
          <DateBound
            label='To'
            value={condition.value?.to ?? ''}
            onChange={(v) => setValue({ ...(condition.value || {}), to: v })}
          />
        </div>
      );

    case 'integer':
      return <RangeRows value={condition.value} onChange={setValue} />;

    case 'money':
      return (
        <div className='flex items-center gap-2'>
          <input
            type='number'
            step='0.01'
            className={inputCls}
            placeholder='from'
            value={condition.value?.from ?? ''}
            onChange={(e) => setValue({ ...(condition.value || {}), from: e.target.value })}
          />
          <span className='text-slate-400 text-xs'>—</span>
          <input
            type='number'
            step='0.01'
            className={inputCls}
            placeholder='to'
            value={condition.value?.to ?? ''}
            onChange={(e) => setValue({ ...(condition.value || {}), to: e.target.value })}
          />
        </div>
      );

    default:
      return (
        <input
          className={inputCls}
          placeholder={['in', 'not_in'].includes(condition.operator) ? 'value1, value2' : 'value'}
          value={
            Array.isArray(condition.value) ? condition.value.join(', ') : condition.value || ''
          }
          onChange={(e) =>
            setValue(
              ['in', 'not_in'].includes(condition.operator)
                ? e.target.value.split(',').map((v) => v.trim()).filter(Boolean)
                : e.target.value
            )
          }
        />
      );
  }
};

const ConditionRow = ({
  attribute, condition, options, unavailable, errors, takenWindows = [], onChange, onRemove
}) => (
  <div className='border border-slate-200 rounded-lg p-3'>
    <div className='flex items-start justify-between gap-3 mb-2'>
      <span className='text-sm font-medium text-slate-800'>{attribute.label}</span>
      <div className='flex items-center gap-2'>
        {attribute.windows && attribute.windows.length > 0 && (
          <select
            className='shadow-sm border border-slate-300 rounded px-2 py-1 text-xs text-slate-700'
            value={condition.window || attribute.windows[0]}
            onChange={(e) => onChange({ ...condition, window: Number(e.target.value) })}
          >
            {attribute.windows.map((w) => (
              <option key={w} value={w} disabled={takenWindows.includes(w)}>
                last {w} days
              </option>
            ))}
          </select>
        )}
        {attribute.units && attribute.units.length > 1 && (
          <select
            className='shadow-sm border border-slate-300 rounded px-2 py-1 text-xs text-slate-700'
            value={condition.unit || attribute.units[0]}
            onChange={(e) => onChange({ ...condition, unit: e.target.value })}
          >
            {attribute.units.map((u) => (
              <option key={u} value={u}>
                {unitLabel(u)}
              </option>
            ))}
          </select>
        )}
        {attribute.units && attribute.units.length === 1 && (
          <span className='text-xs px-2 py-1 rounded bg-slate-100 text-slate-600'>
            {unitLabel(attribute.units[0])}
          </span>
        )}
        {attribute.operators.length > 1 && attribute.type !== 'date' && (
          <select
            className='shadow-sm border border-slate-300 rounded px-2 py-1 text-xs text-slate-700'
            value={condition.operator}
            onChange={(e) => onChange({ ...condition, operator: e.target.value })}
          >
            {attribute.operators.map((op) => (
              <option key={op} value={op}>
                {op === 'eq' ? 'is' : op === 'in' ? 'IN' : op === 'not_in' ? 'NOT IN' : op}
              </option>
            ))}
          </select>
        )}
        <button
          type='button'
          className='text-slate-400 hover:text-red-600 text-sm'
          onClick={onRemove}
          aria-label='Remove condition'
        >
          ×
        </button>
      </div>
    </div>

    <ValueEditor
      attribute={attribute}
      condition={condition}
      options={options}
      unavailable={unavailable}
      onChange={onChange}
    />

    {errors.length > 0 && <p className='text-xs text-red-600 mt-1'>{errors.join('. ')}</p>}
  </div>
);

const ConditionBuilder = ({ library, conditions = [], errors = [], onChange, hasIncludedSegments = false }) => {
  const groups = library?.groups || [];
  const options = library?.options || {};
  const unavailable = library?.options_unavailable || {};
  const byId = {};
  groups.forEach((g) => g.attributes.forEach((a) => { byId[a.id] = a; }));

  // Условия объединяются через AND, поэтому второе условие по тому же атрибуту
  // либо ничего не меняет, либо обнуляет выборку («валюта KES И валюта NGN»).
  // Оконные атрибуты — исключение: 7 и 30 дней это разные условия.
  const windowsOf = (attribute) => attribute.windows || [];

  const takenWindows = (attributeId) => {
    const attribute = byId[attributeId];
    const fallback = windowsOf(attribute)[0];

    return conditions
      .filter((c) => c.attribute === attributeId)
      .map((c) => Number(c.window ?? fallback));
  };

  const isUsed = (attribute) => {
    const used = conditions.some((c) => c.attribute === attribute.id);
    if (!used) return false;
    if (!windowsOf(attribute).length) return true;

    const taken = takenWindows(attribute.id);
    return windowsOf(attribute).every((w) => taken.includes(w));
  };

  const addOptions = groups
    .map((group) => ({
      label: group.label,
      options: group.attributes.filter((a) => !isUsed(a)).map((a) => ({ value: a.id, label: a.label }))
    }))
    .filter((group) => group.options.length > 0);
  const flatAddOptions = addOptions.flatMap((g) =>
    g.options.map((o) => ({ value: o.value, label: `${g.label} · ${o.label}` }))
  );

  const add = (id) => {
    const attribute = byId[id];
    if (!attribute) return;

    const condition = newCondition(attribute);
    // Повторно добавленный оконный атрибут занимает свободный период, а не тот же.
    const free = windowsOf(attribute).find((w) => !takenWindows(id).includes(w));
    if (free) condition.window = free;

    onChange([...conditions, condition]);
  };

  const update = (index, condition) =>
    onChange(conditions.map((c, i) => (i === index ? condition : c)));

  const remove = (index) => onChange(conditions.filter((_, i) => i !== index));

  const errorsFor = (condition) => {
    const label = byId[condition.attribute]?.label;
    return label ? errors.filter((message) => message.startsWith(label)) : [];
  };

  if (!groups.length) {
    return <p className='text-xs text-slate-400'>Loading attributes…</p>;
  }

  return (
    <div className='space-y-3'>
      {conditions.length === 0 && (
        <p className='text-xs text-slate-400'>
          {hasIncludedSegments
            ? 'No attributes yet — includes all players from the included segments, minus exclusions.'
            : 'No conditions yet — the segment would include every player of the selected countries, minus exclusions.'}
        </p>
      )}

      {conditions.map((condition, index) => {
        const attribute = byId[condition.attribute];
        if (!attribute) return null;

        return (
          <ConditionRow
            key={`${condition.attribute}-${index}`}
            attribute={attribute}
            condition={condition}
            options={options}
            unavailable={unavailable}
            takenWindows={takenWindows(condition.attribute).filter(
              (w) => w !== Number(condition.window ?? (byId[condition.attribute]?.windows || [])[0])
            )}
            errors={errorsFor(condition)}
            onChange={(next) => update(index, next)}
            onRemove={() => remove(index)}
          />
        );
      })}

      <div>
        <label className='block text-xs font-medium text-slate-500 mb-1'>Add condition</label>
        {flatAddOptions.length > 0 ? (
          <SearchableSelect
            options={flatAddOptions}
            value=''
            onChange={add}
            placeholder='Pick an attribute…'
          />
        ) : (
          <p className='text-xs text-slate-400'>
            Every attribute is already used — edit or remove a condition above.
          </p>
        )}
      </div>
    </div>
  );
};

const isEmptyCondition = (condition, attribute) => {
  if (!attribute) return true;
  const value = condition.value;

  switch (attribute.type) {
    case 'date':
      return !bound(value?.from) && !bound(value?.to);
    case 'integer':
      return !(value?.ranges || []).some((r) => filled(r.from) || filled(r.to));
    case 'money':
      return !filled(value?.from) && !filled(value?.to);
    default:
      return false;
  }
};

const filled = (v) => v !== undefined && v !== null && String(v).trim() !== '';
const bound = (v) => (v && typeof v === 'object' ? filled(v.days_ago) : filled(v));

export { newCondition, isEmptyCondition, ConditionRow };
export default ConditionBuilder;
